/**
 * About: Vision als roterende tabs.
 *
 * Links korte blokken (role=tab), rechts steeds een tekst (role=tabpanel).
 * De voortgangsbalk in het actieve blok is een CSS-animatie; als die klaar
 * is schuift de rotatie door naar het volgende blok. Pauzeren = de animatie
 * stilzetten (hover, toetsenbordfocus of de pauzeknop), dus JS hoeft geen
 * timer bij te houden.
 *
 * - Draait alleen als de sectie in beeld is.
 * - Klik of pijltjestoets kiest een blok en stopt de rotatie.
 * - prefers-reduced-motion: geen rotatie, alleen handmatige tabs.
 *
 * Idempotent per container via data-vision-bound (Barba re-mounts).
 */
export default function initVisionNav(root = document) {
    const wraps = root.querySelectorAll('[data-vision]');
    if (!wraps.length) return;

    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    wraps.forEach((wrap) => {
        if (wrap.dataset.visionBound === '1') return;
        wrap.dataset.visionBound = '1';

        const tabs = Array.from(wrap.querySelectorAll('[data-vision-tab]'));
        const panels = Array.from(wrap.querySelectorAll('[data-vision-panel]'));
        const toggle = wrap.querySelector('[data-vision-toggle]');
        if (!tabs.length || tabs.length !== panels.length) return;

        let index = 0;
        let playing = !reduce && tabs.length > 1;

        const select = (next, focus = false) => {
            index = (next + tabs.length) % tabs.length;
            tabs.forEach((tab, n) => {
                const on = n === index;
                tab.classList.toggle('is-active', on);
                tab.setAttribute('aria-selected', on ? 'true' : 'false');
                tab.tabIndex = on ? 0 : -1;
            });
            panels.forEach((panel, n) => panel.classList.toggle('is-active', n === index));
            if (focus) tabs[index].focus();
        };

        const setPlaying = (on) => {
            playing = on;
            wrap.classList.toggle('is-paused', !on);
            if (!toggle) return;
            toggle.setAttribute('aria-pressed', on ? 'false' : 'true');
            const label = toggle.querySelector('.about-vision__toggle-label');
            if (label) label.textContent = on ? toggle.dataset.labelPause : toggle.dataset.labelPlay;
        };

        // Volgende blok zodra de voortgangsbalk vol is.
        wrap.addEventListener('animationend', (event) => {
            if (event.animationName !== 'vision-progress' || !playing) return;
            select(index + 1);
        });

        tabs.forEach((tab, n) => {
            tab.addEventListener('click', () => {
                select(n);
                setPlaying(false);
            });
        });

        // Tablist-toetsenbord: pijlen, Home, End.
        wrap.querySelector('[role="tablist"]')?.addEventListener('keydown', (event) => {
            const keys = { ArrowDown: 1, ArrowRight: 1, ArrowUp: -1, ArrowLeft: -1 };
            let next = null;
            if (event.key in keys) next = index + keys[event.key];
            if (event.key === 'Home') next = 0;
            if (event.key === 'End') next = tabs.length - 1;
            if (next === null) return;
            event.preventDefault();
            select(next, true);
            setPlaying(false);
        });

        if (reduce || tabs.length < 2) return;

        if (toggle) {
            toggle.hidden = false;
            toggle.addEventListener('click', () => setPlaying(!playing));
        }

        // Alleen roteren zolang de sectie in beeld is.
        const observer = new IntersectionObserver(
            ([entry]) => wrap.classList.toggle('is-autoplay', entry.isIntersecting),
            { threshold: 0.35 }
        );
        observer.observe(wrap);
    });
}
